import React, { useMemo, useState } from 'react'
import { apiPut } from '../../api'
import { Alert, Button } from '../../components/ui'
import { roundLabel } from '../tournamentStandings'

export const STATUS_LABELS = {
  DRAFT: 'Utkast',
  OPEN: 'Åpen',
  ONGOING: 'Pågår',
  COMPLETED: 'Fullført',
  CANCELLED: 'Avlyst'
}

export const FORMAT_LABELS = {
  ROUND_ROBIN: 'Seriespill',
  KNOCKOUT: 'Cup',
  GROUPS_THEN_KNOCKOUT: 'Grupper + cup'
}

export function fmtDate(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (_) {
    return String(value)
  }
}

export function toLocalInput(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fromLocalInput(value) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

export async function readError(res, fallback) {
  try {
    const body = await res.json()
    return body.message || body.code || fallback
  } catch (_) {
    return fallback
  }
}

/** «0 – 0 (5–4 str.)», «2 – 1 e.o.» eller «2 – 1». */
export function matchScoreText(m, sep = ' – ') {
  if (m.homeScore == null || m.awayScore == null) return null
  const base = `${m.homeScore}${sep}${m.awayScore}`
  if (m.homePenaltyScore != null && m.awayPenaltyScore != null) {
    return `${base} (${m.homePenaltyScore}–${m.awayPenaltyScore} str.)`
  }
  return m.extraTime ? `${base} e.o.` : base
}

/** Hvilken side som vant: 'home' | 'away' | null. */
export function winnerSide(m) {
  if (m.winnerTeamId) {
    if (m.winnerTeamId === m.homeTeamId) return 'home'
    if (m.winnerTeamId === m.awayTeamId) return 'away'
    return null
  }
  if (m.status !== 'COMPLETED' || m.homeScore == null || m.awayScore == null) return null
  if (m.homeScore > m.awayScore) return 'home'
  if (m.awayScore > m.homeScore) return 'away'
  if (m.homePenaltyScore != null && m.awayPenaltyScore != null) {
    if (m.homePenaltyScore > m.awayPenaltyScore) return 'home'
    if (m.awayPenaltyScore > m.homePenaltyScore) return 'away'
  }
  return null
}

/** Navn med PSN-ID (eller Spelaren-navn når navnet er PSN-ID) under, i esport. */
export function TeamLabel({ name, subtitle, align, fallback = 'TBD' }) {
  return (
    <span className="tn-team-label" style={align ? { textAlign: align } : undefined}>
      <span className="tn-team-name">{name || fallback}</span>
      {subtitle && subtitle !== name && (
        <span className="tn-team-subtitle">{subtitle}</span>
      )}
    </span>
  )
}

/** Cupkamp med likt resultat: må avgjøres på straffer. */
export function needsPenalties(match, home, away) {
  if (!match?.isKnockout) return false
  if (home === '' || away === '' || home == null || away == null) return false
  return Number(home) === Number(away)
}

/** Straffe-/ekstraomgang-felter for cupkamper. */
export function KnockoutExtras({ match, home, away, penHome, penAway, extraTime, onChange, disabled, hint }) {
  if (!match?.isKnockout) return null
  const showPens = needsPenalties(match, home, away)
  return (
    <div className="tn-ko-extras">
      <label className="check">
        <input
          type="checkbox"
          checked={!!extraTime}
          onChange={(e) => onChange('extraTime', e.target.checked)}
          disabled={disabled}
        />
        Ekstraomganger
      </label>
      {showPens && (
        <div className="tn-pens">
          <span className="tn-pens-title">
            Straffer{hint && <span className="muted"> · vises ved uavgjort</span>}
          </span>
          <div className="tn-pair">
            <label className="field">
              <span className="field-label tn-small-label">Hjemme</span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                className="tn-score-input sm"
                placeholder="Hjemme"
                value={penHome ?? ''}
                onChange={(e) => onChange('penHome', e.target.value)}
                disabled={disabled}
              />
            </label>
            <label className="field">
              <span className="field-label tn-small-label">Borte</span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                className="tn-score-input sm"
                placeholder="Borte"
                value={penAway ?? ''}
                onChange={(e) => onChange('penAway', e.target.value)}
                disabled={disabled}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  )
}

/** Bygger body til PUT /score. Returnerer { error } ved ugyldig input. */
export function buildScoreBody(match, home, away, penHome, penAway, extraTime) {
  if (home === '' || away === '' || home == null || away == null) return { error: 'Fyll inn resultat.' }
  const h = Number(home)
  const a = Number(away)
  if (Number.isNaN(h) || Number.isNaN(a)) return { error: 'Ugyldig resultat.' }
  const body = { homeScore: h, awayScore: a }
  if (match?.isKnockout) {
    body.extraTime = !!extraTime
    if (h === a) {
      const ph = Number(penHome)
      const pa = Number(penAway)
      if (penHome === '' || penAway === '' || penHome == null || penAway == null || Number.isNaN(ph) || Number.isNaN(pa)) {
        return { error: 'Cupkamper kan ikke ende uavgjort. Fyll inn resultatet på straffer.' }
      }
      if (ph === pa) return { error: 'Straffekonkurransen må ha en vinner.' }
      body.homePenaltyScore = ph
      body.awayPenaltyScore = pa
    }
  }
  return { body }
}

/** Tabell (# Lag K V U T +/− P). advancing: antall plasser som markeres som «videre». */
export function StandingsTable({ title, rows, compact, advancing = 0, framed }) {
  return (
    <div className={`tn-standings ${compact ? 'compact' : ''} ${framed ? 'framed' : ''}`}>
      {title && <div className={framed ? 'tn-standings-title framed' : 'eyebrow'}>{title}</div>}
      <div className="tn-standings-scroll">
        <table className="tn-standings-table">
          <thead>
            <tr>
              <th className="l" scope="col">#</th>
              <th className="l" scope="col">Lag</th>
              <th scope="col"><abbr title="Kamper">K</abbr></th>
              <th scope="col"><abbr title="Vunnet">V</abbr></th>
              <th scope="col"><abbr title="Uavgjort">U</abbr></th>
              <th scope="col"><abbr title="Tapt">T</abbr></th>
              <th scope="col"><abbr title="Målforskjell">+/−</abbr></th>
              <th scope="col"><abbr title="Poeng">P</abbr></th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r, i) => {
              const diff = r.goalsFor - r.goalsAgainst
              return (
                <tr key={r.teamId}>
                  <td className="l">
                    <span className={`tn-pos ${i < advancing ? 'adv' : ''}`}>{i + 1}</span>
                  </td>
                  <td className="l tn-standings-team">
                    <span className="tn-team-name">{r.teamName}</span>
                    {r.subtitle && r.subtitle !== r.teamName && (
                      <span className="tn-team-subtitle">{r.subtitle}</span>
                    )}
                  </td>
                  <td>{r.played}</td>
                  <td>{r.won}</td>
                  <td>{r.drawn}</td>
                  <td>{r.lost}</td>
                  <td>{diff > 0 ? `+${diff}` : diff < 0 ? `−${Math.abs(diff)}` : '0'}</td>
                  <td className="tn-points">{r.points}</td>
                </tr>
              )
            })}
            {(!rows || rows.length === 0) && (
              <tr><td colSpan={8} className="tn-empty-cell">Ingen data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const formatScheduleLabel = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/** Kampliste med tid/bane og resultatregistrering (brukes i Oppsett og Kamper). */
export function MatchScoreList({ tournament, matches, canManage, saving, run, onReload, groupByRound, grid }) {
  const [scores, setScores] = useState({})
  const [scoreErrors, setScoreErrors] = useState({})
  const [schedules, setSchedules] = useState({})
  const [open, setOpen] = useState({})
  const rounds = useMemo(
    () => Array.from(new Set(matches.map((m) => m.roundNumber))).sort((a, b) => a - b),
    [matches]
  )

  const setScoreField = (matchId, side, value) => {
    setScores((prev) => ({
      ...prev,
      [matchId]: { ...(prev[matchId] || {}), [side]: value }
    }))
  }

  const setScheduleField = (matchId, field, value) => {
    setSchedules((prev) => ({
      ...prev,
      [matchId]: { ...(prev[matchId] || {}), [field]: value }
    }))
  }

  /** Gjeldende (redigert eller lagret) verdi for et resultatfelt. */
  const scoreValue = (match, field) => {
    const entry = scores[match.id] || {}
    if (entry[field] != null) return entry[field]
    const saved = {
      home: match.homeScore,
      away: match.awayScore,
      penHome: match.homePenaltyScore,
      penAway: match.awayPenaltyScore,
      extraTime: match.extraTime
    }[field]
    if (field === 'extraTime') return !!saved
    return saved != null ? String(saved) : ''
  }

  const saveScore = (match) => {
    const { body, error } = buildScoreBody(
      match,
      scoreValue(match, 'home'),
      scoreValue(match, 'away'),
      scoreValue(match, 'penHome'),
      scoreValue(match, 'penAway'),
      scoreValue(match, 'extraTime')
    )
    setScoreErrors((prev) => ({ ...prev, [match.id]: error || null }))
    if (error) return
    run(async () => {
      const res = await apiPut(
        `/api/tournaments/${tournament.id}/matches/${match.id}/score`,
        body
      )
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke lagre resultat'))
      await onReload()
    }, 'Resultat lagret')
  }

  const saveSchedule = (match) => {
    const entry = schedules[match.id] || {}
    const localValue = entry.scheduledAtLocal != null
      ? entry.scheduledAtLocal
      : toLocalInput(match.scheduledAt)
    const courtName = entry.courtName != null ? entry.courtName : (match.courtName || '')
    run(async () => {
      const res = await apiPut(
        `/api/tournaments/${tournament.id}/matches/${match.id}/schedule`,
        {
          scheduledAt: fromLocalInput(localValue),
          courtName: courtName.trim() || null
        }
      )
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke lagre tid/bane'))
      await onReload()
    }, 'Tid og bane lagret')
  }

  const clearSchedule = (match) => {
    run(async () => {
      const res = await apiPut(
        `/api/tournaments/${tournament.id}/matches/${match.id}/schedule`,
        { scheduledAt: null, courtName: null }
      )
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke fjerne tid/bane'))
      setSchedules((prev) => ({
        ...prev,
        [match.id]: { scheduledAtLocal: '', courtName: '' }
      }))
      await onReload()
    }, 'Tid og bane fjernet')
  }

  const renderMatch = (match) => {
    const scheduleEntry = schedules[match.id] || {}
    const scheduleLabel = formatScheduleLabel(match.scheduledAt)
    const score = matchScoreText(match)
    const win = winnerSide(match)
    const editable = canManage && match.homeTeamId && match.awayTeamId
    const isOpen = !!open[match.id]
    const panelId = `tn-edit-${match.id}`
    return (
      <div key={match.id} className="tn-match">
        <div className="tn-match-line">
          <div className="tn-match-teams">
            <span className={`tn-match-side home ${win === 'home' ? 'win' : ''}`}>
              <TeamLabel name={match.homeTeamName} subtitle={match.homeTeamSubtitle} fallback="—" align="right" />
            </span>
            <span className={`tn-score-badge ${score ? 'played' : ''}`}>{score || 'vs'}</span>
            <span className={`tn-match-side away ${win === 'away' ? 'win' : ''}`}>
              <TeamLabel name={match.awayTeamName} subtitle={match.awayTeamSubtitle} fallback="—" />
            </span>
          </div>
          {(scheduleLabel || match.courtName) && (
            <div className="tn-match-meta">
              {scheduleLabel && <span>Start: {scheduleLabel}</span>}
              {match.courtName && <span>Bane: {match.courtName}</span>}
            </div>
          )}
          {editable && (
            <Button
              variant={isOpen ? 'soft' : 'tertiary'}
              size="sm"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpen((prev) => ({ ...prev, [match.id]: !isOpen }))}
            >
              {isOpen ? 'Lukk' : 'Rediger'}
            </Button>
          )}
        </div>
        {editable && isOpen && (
          <div className="tn-match-edit" id={panelId}>
            <div className="tn-edit-block">
              <span className="eyebrow">Tid og bane</span>
              <div className="tn-edit-fields">
                <label className="field tn-grow-2">
                  <span className="field-label tn-small-label">Starttid</span>
                  <input
                    type="datetime-local"
                    value={
                      scheduleEntry.scheduledAtLocal != null
                        ? scheduleEntry.scheduledAtLocal
                        : toLocalInput(match.scheduledAt)
                    }
                    onChange={(e) => setScheduleField(match.id, 'scheduledAtLocal', e.target.value)}
                    disabled={saving}
                  />
                </label>
                <label className="field tn-grow-1">
                  <span className="field-label tn-small-label">Bane</span>
                  <input
                    type="text"
                    className="input-mono"
                    placeholder="Bane"
                    value={
                      scheduleEntry.courtName != null
                        ? scheduleEntry.courtName
                        : (match.courtName || '')
                    }
                    onChange={(e) => setScheduleField(match.id, 'courtName', e.target.value)}
                    disabled={saving}
                  />
                </label>
              </div>
              <div className="row">
                <Button variant="secondary" size="sm" disabled={saving} onClick={() => saveSchedule(match)}>
                  Lagre tid/bane
                </Button>
                {(match.scheduledAt || match.courtName) && (
                  <Button variant="danger-outline" size="sm" disabled={saving} onClick={() => clearSchedule(match)}>
                    Fjern
                  </Button>
                )}
              </div>
            </div>
            <div className="tn-edit-block">
              <span className="eyebrow">Resultat</span>
              <div className="tn-edit-fields">
                <label className="field tn-grow-1">
                  <span className="field-label tn-small-label">Hjemme</span>
                  <input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    className="tn-score-input"
                    placeholder="Hjemme"
                    value={scoreValue(match, 'home')}
                    onChange={(e) => setScoreField(match.id, 'home', e.target.value)}
                    disabled={saving}
                  />
                </label>
                <label className="field tn-grow-1">
                  <span className="field-label tn-small-label">Borte</span>
                  <input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    className="tn-score-input"
                    placeholder="Borte"
                    value={scoreValue(match, 'away')}
                    onChange={(e) => setScoreField(match.id, 'away', e.target.value)}
                    disabled={saving}
                  />
                </label>
                <Button className="tn-grow-1 tn-save-score" size="sm" disabled={saving} onClick={() => saveScore(match)}>
                  Lagre resultat
                </Button>
              </div>
              <KnockoutExtras
                match={match}
                home={scoreValue(match, 'home')}
                away={scoreValue(match, 'away')}
                penHome={scoreValue(match, 'penHome')}
                penAway={scoreValue(match, 'penAway')}
                extraTime={scoreValue(match, 'extraTime')}
                onChange={(field, value) => setScoreField(match.id, field, value)}
                disabled={saving}
              />
              {scoreErrors[match.id] && <Alert tone="error">{scoreErrors[match.id]}</Alert>}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (!matches.length) {
    return <p className="tn-empty-text">Ingen kamper ennå.</p>
  }

  if (groupByRound) {
    const blocks = rounds.map((round) => (
      <div key={round} className="tn-round-block">
        <h3 className="tn-round-title">{roundLabel(tournament, round)}</h3>
        {matches.filter((m) => m.roundNumber === round).map(renderMatch)}
      </div>
    ))
    return grid ? <div className="tn-round-grid">{blocks}</div> : blocks
  }

  return <div className="tn-round-block">{matches.map(renderMatch)}</div>
}
